import {useAppDataStore} from "@/mobx/appDataStore.jsx";
import {useEffect, useState} from "react";
import {observer} from "mobx-react";
import moment from "moment";

export default observer(function AdminGeoAllowedIps() {
  const appDataStore = useAppDataStore();
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ip: "", description: ""});

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  useEffect(() => {
    loadIps();
  }, []);

  const loadIps = async () => {
    try {
      setLoading(true);
      const res = await appDataStore.get('/geo-allowed-ips/list');
      setIps(res.ips || []);
    } catch (err) {
      alert('Error al cargar IPs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.ip.trim()) return;
    try {
      setSaving(true);
      await appDataStore.post('/geo-allowed-ips/create', {ip: form.ip.trim(), description: form.description.trim()});
      setForm({ip: "", description: ""});
      await loadIps();
    } catch (err) {
      alert('Error al agregar IP: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (record) => {
    try {
      await appDataStore.post('/geo-allowed-ips/update', {id: record.id, enabled: !record.enabled});
      setIps(prev => prev.map(i => i.id === record.id ? {...i, enabled: !record.enabled} : i));
    } catch (err) {
      alert('Error al actualizar IP: ' + err.message);
    }
  };

  const handleDelete = async (record) => {
    if (!confirm(`¿Eliminar la IP ${record.ip}?`)) return;
    try {
      await appDataStore.post('/geo-allowed-ips/delete', {id: record.id});
      setIps(prev => prev.filter(i => i.id !== record.id));
    } catch (err) {
      alert('Error al eliminar IP: ' + err.message);
    }
  };

  if (!hasMasterScope) {
    return (
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">IPs Permitidas</h2>
        <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
          <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
          <p className="text-red-700">Se requiere el scope <strong>Master</strong>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 max-w-5xl mx-auto text-black">
      <h2 className="text-2xl font-bold mb-2 text-gray-800">IPs Permitidas</h2>
      <p className="text-sm text-gray-500 mb-6">
        Estas IPs o rangos (formato CIDR, ej: 190.1.2.0/24) pueden redimir tokens aunque su país no esté en la lista de países permitidos.
      </p>

      <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3 mb-6 border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col">
          <label className="text-xs font-semibold text-gray-600 mb-1">IP o rango</label>
          <input
            type="text"
            value={form.ip}
            onChange={e => setForm(prev => ({...prev, ip: e.target.value}))}
            placeholder="190.1.2.3 o 190.1.2.0/24"
            className="px-3 py-2 border border-gray-300 rounded text-sm w-56 font-mono"
          />
        </div>
        <div className="flex flex-col flex-1 min-w-[220px]">
          <label className="text-xs font-semibold text-gray-600 mb-1">Descripción</label>
          <input
            type="text"
            value={form.description}
            onChange={e => setForm(prev => ({...prev, description: e.target.value}))}
            placeholder="Ej: oficina Miami, cliente X"
            className="px-3 py-2 border border-gray-300 rounded text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={saving || !form.ip.trim()}
          className="px-4 py-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-50 text-white rounded text-sm font-medium"
        >
          {saving ? 'Agregando...' : 'Agregar IP'}
        </button>
      </form>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando IPs...</div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">IP / Rango</th>
                <th className="text-left px-4 py-3">Descripción</th>
                <th className="text-left px-4 py-3">Estado</th>
                <th className="text-left px-4 py-3">Fecha Creación</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ips.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-gray-400">No hay IPs registradas</td>
                </tr>
              )}
              {ips.map(record => (
                <tr key={record.id}>
                  <td className="px-4 py-3 font-mono">{record.ip}</td>
                  <td className="px-4 py-3 text-gray-600">{record.description || '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggle(record)}
                      className={`px-2 py-1 rounded text-xs font-medium ${record.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {record.enabled ? 'Activa' : 'Inactiva'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{moment(record.createdAt).format('YYYY-MM-DD HH:mm')}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(record)}
                      className="px-3 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});
